import { faExclamationCircle, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PushWarningNotification, styles } from "@repo/helpers";
import { Button, ButtonProps, ButtonSize } from "@repo/ui";
import { Children, ComponentElement, MouseEvent, PropsWithChildren, useCallback, useMemo } from "react";

import { useValidationContext } from "../contexts/validation";
import style from "./style.module.scss";
import { ButtonContainerProps, DEButtonProps } from "./types";

export function ButtonContainer(props: PropsWithChildren<ButtonContainerProps>) {
	const { submitting, submitError, completed, hideAll, center } = props;
	const { buttonSize = ButtonSize.large } = props;
	const containerClass = styles(style.buttonContainer, center && style.center);

	const buttons = useMemo(
		() =>
			Children.map(props.children, (c: ComponentElement<ButtonProps, any>) => {
				if (!c?.props?.onClick) return c;
				const cProps = {
					...c.props,
					size: c.props.size ?? buttonSize,
					completed: completed,
				};
				return { ...c, props: cProps };
			}).filter((a) => a),
		[buttonSize, completed, props.children],
	);

	if (hideAll) return null;

	if (submitting) {
		return (
			<h1 className={containerClass}>
				Saving...
				<FontAwesomeIcon icon={faSpinner} spin />
			</h1>
		);
	}

	return (
		<div className={containerClass}>
			{submitError && (
				<p className={style.error} style={{ display: "inline" }}>
					<FontAwesomeIcon icon={faExclamationCircle} /> {submitError}{" "}
				</p>
			)}
			{buttons}
		</div>
	);
}

export function DEButton(props: DEButtonProps) {
	const { completed, mustBeComplete, children, disabled, onClick, hidden, ...btnProps } = props;
	const { validateOnSave, setValidationTriggered } = useValidationContext();

	const canClick = !mustBeComplete || completed || validateOnSave;

	const trySubmit = useCallback(
		function (e: MouseEvent<HTMLButtonElement>) {
			if (mustBeComplete && !completed) {
				setValidationTriggered();
				PushWarningNotification("Please correct form errors");
			} else props.onClick(e);
		},
		[onClick, mustBeComplete, completed, setValidationTriggered],
	);

	if (hidden) return null;

	return (
		<Button key={props.id} disabled={disabled || !canClick} onClick={trySubmit} {...btnProps}>
			{children}
		</Button>
	);
}
